import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersist from 'vuex-persist';
import {AuthError} from '../custom-errors.js';
Vue.use(Vuex);

const vuexPersist = new VuexPersist({
    key: 'my-app',
    storage: window.localStorage,
    reducer: (state) => ({
        accessToken: state.accessToken,
        tokenType: state.tokenType,
        currentUser: state.currentUser,
        apiURL: state.apiURL,
        items: state.items,
        commentDetails: state.commentDetails,
        responseMessage: state.responseMessage,
    })
});

const store = new Vuex.Store({
    plugins: [vuexPersist.plugin],
    state: {
        accessToken: null,
        tokenType: null,
        currentUser: null,
        apiURL: `${window.location.origin}/eqtl/api`,
        // apiURL: "http://localhost:9090/api", // url for local development
        items: [],
        responseMessage: {
            message: "",
            type: ""
        },
        commentDetails: {
            user_name: "",
            credible_set: "",
            molecular_trait_id: "",
            variant: "",
            comment: "",
            event_type: null,
            id_1: "",
            id_2: "",
        }
    },
    mutations: {
        setresponseMessage(state, message) {
            state.responseMessage = message;
        },
        setCurrentUser(state, username) {
            state.currentUser = username;   

        },
        setAccessToken(state, token) {
            state.accessToken = token;
        },
        setTokenType(state, type) {
            state.tokenType = type;
        },
        addItem(state, item) {
            state.items.push(item);
        },
        setItems(state, items) {
            state.items = items;
        },
        updateCommentDetails(state, { key, value }) {
            state.commentDetails[key] = value;
        },
        clearCommentDetails(state) {
            state.commentDetails = {
                user_name: "",
                credible_set: "",
                molecular_trait_id: "",
                variant: "",
                comment: "",
                event_type: "",
                id_1: "",
                id_2: ""
            };
        },
    },
    actions: {
        async login({ commit, state }, credentials) {
            const response = await fetch(`${state.apiURL}/admin`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(credentials)
            });

            const data = await response.json();
            if (response.ok) {
                commit('setAccessToken', data.access_token);  // Store access token in Vuex
                commit('setCurrentUser', data.username);  // Store current user in Vuex
                commit('setTokenType', data.token_type);  // Store token type in Vuex
            } else {
                throw new Error(data.detail || 'Failed to login');
            }
        },
        handleRefreshFailure({ commit }, errorDetail) {
            // not using errorDetail yet, but could be useful later
            commit('setAccessToken', null);  
            commit('setTokenType', null);  
        },
        async refreshAccessToken({ commit, state, dispatch }) {
            try {
                const response = await fetch(`${state.apiURL}/refresh`, {
                    method: 'POST',
                    credentials: 'include'
                });
                const data = await response.json();
                if (!response.ok) {
                    if (response.status === 401) {
                        // Throw with a fixed message and include the response
                        throw new AuthError('Unauthorized', response);
                    }
                    // Handle other non-OK responses
                    dispatch('handleRefreshFailure', data.detail || 'Failed to refresh token');
                    return false;
                }
                // Update state with the new token information on success
                commit('setAccessToken', data.access_token);
                commit('setCurrentUser', data.username);
                commit('setTokenType', data.token_type);
                return true;
            } catch (error) {
                if (error.status === 401) {
                    throw new AuthError('Unauthorized', error);
                }
                throw error;
            }
        },
        
        async logout({ commit, state }) {
            // Make backend logout request to /logout route
            try {
                const response = await fetch(`${state.apiURL}/logout`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Authorization': `Bearer ${state.accessToken}`
                    }
                });
                
                if (!response.ok) {
                    const data = await response.json();
                    throw new Error(data.detail || 'Failed to logout');
                }else{
                    // Clear Vuex state
                    commit('setAccessToken', null);
                    commit('setTokenType', null);
                    commit('setCurrentUser', null);
                    commit('clearCommentDetails')
                    commit("setresponseMessage", { message: "", type: "" })
                }
            } catch (error) {
                console.error('Logout failed:', error);
            }
        },
        updateResponseMessage({ commit }, message) {
            commit('setresponseMessage', message);
        },
        addItem({ commit }, item) {
            commit('addItem', item);
        },
        updateCommentDetail({ commit }, { key, value }) {
            commit('updateCommentDetails', { key, value });
        },
        clearCommentDetails({ commit }) {
            commit('clearCommentDetails');
        },
        addId({ commit, state }, id) {
            if (!state.commentDetails.id_1) {
            commit('updateCommentDetails', { key: 'id_1', value: id });
            } else if (!state.commentDetails.id_2) {
            commit('updateCommentDetails', { key: 'id_2', value: id });
            } else {
            console.log("Both ID fields are already filled.");
            }
        },
        setCommentDetails({ commit }, commentData) {
            Object.keys(commentData).forEach(key => {
                commit('updateCommentDetails', { key, value: commentData[key] });
            });
        },
    },
    getters: {
        isAuthenticated: state => !!state.accessToken,
        getLastRoute(state) {
            return state.lastRoute;
          },
          getSelectedRowData(state) {
            return state.selectedRowData;
          },
    }
});

export default store;
